import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, MapPin, Swords, Users, Scroll, User } from 'lucide-react';
import type { CampaignData } from '../../../shared/types';

type EntitySuggestion = {
  name: string;
  type: string; // 'wiki' | 'npc' | 'location' | 'organization' | 'plotThread' | 'character'
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  wiki: <BookOpen size={12} />,
  npc: <User size={12} />,
  location: <MapPin size={12} />,
  organization: <Users size={12} />,
  plotThread: <Scroll size={12} />,
  character: <Swords size={12} />,
};

const TYPE_LABELS: Record<string, string> = {
  wiki: 'Wiki',
  npc: 'NPC',
  location: 'Location',
  organization: 'Org',
  plotThread: 'Plot',
  character: 'PC',
};

function buildSuggestions(data: CampaignData): EntitySuggestion[] {
  const suggestions: EntitySuggestion[] = [];
  for (const a of data.wikiArticles || []) {
    suggestions.push({ name: a.title, type: 'wiki' });
  }
  for (const n of data.npcs || []) {
    suggestions.push({ name: n.name, type: 'npc' });
  }
  for (const l of data.locations || []) {
    suggestions.push({ name: l.name, type: 'location' });
  }
  for (const o of data.organizations || []) {
    suggestions.push({ name: o.name, type: 'organization' });
  }
  for (const p of data.plotThreads || []) {
    suggestions.push({ name: p.title, type: 'plotThread' });
  }
  for (const pc of data.playerCharacters || []) {
    suggestions.push({ name: pc.characterName, type: 'character' });
  }
  // Deduplicate by name
  const seen = new Set<string>();
  return suggestions.filter(s => {
    const key = s.name.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function fuzzyMatch(query: string, text: string): boolean {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t.includes(q)) return true;
  // Simple fuzzy: all query chars appear in order
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

// ── Hook: useWikiLinkAutocomplete ────────────────────────────────────

type AutocompleteState = {
  isOpen: boolean;
  query: string;
  triggerStart: number; // index of first `[` in `[[`
  selectedIndex: number;
};

export function useWikiLinkAutocomplete(
  campaignData: CampaignData,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  value: string,
  onChange: (value: string) => void,
) {
  const [state, setState] = useState<AutocompleteState>({
    isOpen: false,
    query: '',
    triggerStart: -1,
    selectedIndex: 0,
  });
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  const allSuggestions = useMemo(() => buildSuggestions(campaignData), [campaignData]);

  const filtered = useMemo(() => {
    if (!state.isOpen) return [];
    const q = state.query;
    if (!q) return allSuggestions.slice(0, 20);
    return allSuggestions
      .filter(s => fuzzyMatch(q, s.name))
      .slice(0, 20);
  }, [allSuggestions, state.isOpen, state.query]);

  const updateDropdownPosition = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Measure caret position using a hidden mirror div
    const rect = textarea.getBoundingClientRect();
    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;

    // Approximate position based on cursor index
    const text = textarea.value.substring(0, textarea.selectionStart);
    const lines = text.split('\n');
    const currentLine = lines.length - 1;

    const top = rect.top + (currentLine + 1) * lineHeight - textarea.scrollTop + window.scrollY;
    const left = rect.left + 16; // offset slightly from left

    setDropdownPos({ top, left });
  }, [textareaRef]);

  const close = useCallback(() => {
    setState({ isOpen: false, query: '', triggerStart: -1, selectedIndex: 0 });
    setDropdownPos(null);
  }, []);

  const insertLink = useCallback((name: string) => {
    const textarea = textareaRef.current;
    if (!textarea || state.triggerStart < 0) return;

    const before = value.substring(0, state.triggerStart);
    const after = value.substring(textarea.selectionStart);
    const newValue = `${before}[[${name}]]${after}`;
    onChange(newValue);
    close();

    // Set cursor position after the inserted link
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = state.triggerStart + name.length + 4; // [[ + name + ]]
        textareaRef.current.selectionStart = pos;
        textareaRef.current.selectionEnd = pos;
        textareaRef.current.focus();
      }
    });
  }, [textareaRef, state.triggerStart, value, onChange, close]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!state.isOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setState(prev => ({
        ...prev,
        selectedIndex: Math.min(prev.selectedIndex + 1, filtered.length - 1),
      }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setState(prev => ({
        ...prev,
        selectedIndex: Math.max(prev.selectedIndex - 1, 0),
      }));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (filtered.length > 0) {
        e.preventDefault();
        insertLink(filtered[state.selectedIndex]?.name || filtered[0].name);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  }, [state.isOpen, state.selectedIndex, filtered, insertLink, close]);

  const handleInput = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const text = textarea.value;

    // Look backward from cursor for `[[`
    const beforeCursor = text.substring(0, cursorPos);
    const lastOpen = beforeCursor.lastIndexOf('[[');

    if (lastOpen >= 0) {
      const between = beforeCursor.substring(lastOpen + 2);
      // Only open if there's no `]]` between `[[` and cursor, and no newlines
      if (!between.includes(']]') && !between.includes('\n') && between.length <= 60) {
        setState({
          isOpen: true,
          query: between,
          triggerStart: lastOpen,
          selectedIndex: 0,
        });
        updateDropdownPosition();
        return;
      }
    }

    if (state.isOpen) {
      close();
    }
  }, [textareaRef, state.isOpen, close, updateDropdownPosition]);

  return {
    autocompleteState: state,
    filtered,
    dropdownPos,
    handleKeyDown,
    handleInput,
    insertLink,
    close,
  };
}

// ── Dropdown Component ───────────────────────────────────────────────

type WikiLinkDropdownProps = {
  suggestions: EntitySuggestion[];
  selectedIndex: number;
  position: { top: number; left: number } | null;
  onSelect: (name: string) => void;
  onClose: () => void;
};

export function WikiLinkDropdown({
  suggestions,
  selectedIndex,
  position,
  onSelect,
  onClose,
}: WikiLinkDropdownProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const item = list.children[selectedIndex] as HTMLElement | undefined;
    if (item) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  if (!position || suggestions.length === 0) return null;

  return createPortal(
    <div
      ref={listRef}
      className="fixed z-[9999] bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg shadow-xl overflow-y-auto"
      style={{
        top: position.top,
        left: position.left,
        maxHeight: 240,
        minWidth: 220,
        maxWidth: 340,
      }}
    >
      {suggestions.map((s, i) => (
        <button
          key={`${s.type}-${s.name}`}
          type="button"
          className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
            i === selectedIndex
              ? 'bg-[var(--accent-muted)] text-[var(--accent-text)]'
              : 'text-[var(--text)] hover:bg-[var(--bg-surface-alt)]'
          }`}
          onMouseDown={(e) => {
            e.preventDefault(); // prevent textarea blur
            onSelect(s.name);
          }}
        >
          <span className="text-[var(--text-muted)] flex-shrink-0">{TYPE_ICONS[s.type]}</span>
          <span className="truncate flex-1">{s.name}</span>
          <span className="text-[10px] text-[var(--text-muted)] flex-shrink-0">{TYPE_LABELS[s.type]}</span>
        </button>
      ))}
    </div>,
    document.body,
  );
}
